// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import { Group, RoleGroup } from '@app/access-management/core/models/groups.model';

export interface Loadable {
  status: LoadingStatus;
  data?: any;
}

export interface ProjectListItem extends Loadable {
  id: string;
  name: string;
  logo: string;
  sector?: string;
}

export interface ProjectDetail extends ProjectListItem {
  website?: string;
  members: Members;
  membershipTiers: MembershipTier[];
}

export type MaybeLoadable = Loadable | undefined;

export function isLoadable(maybeLoadable: any): maybeLoadable is Loadable {
  return maybeLoadable && maybeLoadable.status;
}

export function needsLoading<T>(data: MaybeLoadable | T) {
  return !isLoadable(data) || data.status === LoadingStatus.LOAD_FAILED;
}

export enum LoadingStatus {
  LOADING = 'loading',
  LOAD_FAILED = 'load failed',
  LOAD_COMPLETE = 'loaded'
}

export interface ContractRequest {
  returnURL: string;
  primaryContact: MemberContact;
}

export interface ContractResponse {
  url: string;
  envelopeId: string;
}

export interface OnboardingResponse {
  envelopeId: string;
  statusCode: number;
  message: string;
  entities: OnboardingEntities;
}

export interface OnboardingEntities {
  'Account.Id': string;
  'Opportunity.Id': string;
  'Opportunity.Name': string;
  contacts: OnboardingContact[];
}

export interface OnboardingContact {
  'Contact.Id': string;
  'Contact.Email': string;
}

export interface MembershipFormCompleted {
  tier: SubtierId;
  tierName: string;
  companyName: string;
  companyAddress: Address;
  billingAddress: Address;
  primaryContact: MemberContact;
  billingContact: MemberContact;
  technicalContact?: MemberContact;
  marketingContact?: MemberContact;
  prContact?: MemberContact;
  legalContact?: MemberContact;
}

export interface Members {
  [membershipType: string]: Member[];
}

export interface Member {
  id: string;
  name: string;
  membershipTier: string;
}

export interface MembershipTierDetails {
  projectId: string;
  membershipTiers: MembershipTier[];
}

export interface MembershipTier {
  name: string;
  id: string;
  subtiers: MembershipSubtier[];
}

export interface MembershipTierSelected extends MembershipTier {
  subtier: MembershipSubtier;
}

export interface MembershipSubtier {
  id: SubtierId;
  price: Price;
  minCompanySize?: number;
  maxCompanySize?: number;
  description?: string;
}

export type SubtierId = string;

export interface Price {
  currency: string;
  amount: number;
}

export class USDPrice implements Price {
  currency = 'USD';
  constructor(public amount: number) {}
}

export interface Address {
  street: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
}

export interface MemberContact {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  email: string;
  jobTitle: string;
}

export interface CompanyInfo {
  companyName: string;
  companyAddress: Address;
}

export interface Contacts {
  primaryContact: MemberContact;
  technicalContact: MemberContact;
  billingContact: MemberContact;
  billingAddress: Address;
  marketingContact: MemberContact;
  prContact?: MemberContact;
  legalContact?: MemberContact;
}

export function emptyContact() {
  return {
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    jobTitle: ''
  };
}

export function emptyAddress() {
  return {
    street: '',
    city: '',
    state: '',
    zipCode: '',
    country: ''
  };
}

export interface UserUpdates {
  [updatedKey: string]: any;
}

export interface UserRole {
  name: string;
  id: string;
  title: string;
}

export interface Role extends UserRole {
  privilegeLevel: number;
  description: string;
  numberOfUsers: number;
  updatedAt: string;
  createdAt: string;
  createdBy: string;
  role_objects: RoleObject[];
}

export interface IAMUser {
  id: string;
  name: string;
  email: string;
  username: string;
  accountName: string;
  object_type_id?: string;
  object_type_name?: string;
  object_id?: string;
  scope_id?: string;
  roles: UserRole[];
}

export interface RoleUserScope {
  id: string;
  name: string;
  email: string;
  username: string;
  accountName: string;
  objectID?: string;
  objectTypeID?: number;
  objectTypeName?: string;
  objectName?: string;
  scopeID?: string;
}

export interface PolicyListResponse {
  policies: Policy[];
}

// Policy List response
export interface Policy {
  id: string;
  name: string;
  description: string;
  numUsers: boolean;
  username: string;
}

export interface PolicyStatement {
  effect: string;
  resourceActions: ResourceAction[];
}

export interface ResourceAction {
  resource: PolicyAction;
  action: PolicyAction;
  service: PolicyAction;
}

export interface PolicyAction {
  id: string;
  name: string;
}

// Policy Detail inside Redux Store
export interface PolicyDetail {
  policy: PolicyDetailResponse;
}

export interface PolicyDetailResponse {
  policy_id: string;
  policy_name: string;
  policy_type: string;
  description: string;
  statement: StatementResponse[];
  created_at: string;
  updated_at: string;
}

export interface StatementResponse {
  statement_id: string;
  effect: string;
  resource_id: string;
  resource_name?: string;
  service_id: string;
  service_name: string;
  actions: ActionStatement[];
  created_at: string;
  updated_at: string;
}

export interface ActionStatement {
  id: string;
  name: string;
}

export interface Loaded<T> extends Loadable {
  status: LoadingStatus.LOAD_COMPLETE;
  data: T;
}

export interface RoleDetail {
  role?: Role;
  users?: RoleUserScope[];
  policies?: Policy[];
  groups?: RoleGroup[];
}

export interface ScopeCategory {
  id: string;
  name: string;
}

export interface ScopeConstraint {
  id: string;
  name: string;
  scopeId?: string;
}

export interface RoleUserSelections {
  users?: IAMUser[];
  category?: ScopeCategory;
  constraints?: ScopeConstraint[];
}

export interface RoleGroupSelections {
  groups?: Group[];
  category?: ScopeCategory;
  constraints?: ScopeConstraint[];
}

export enum AlertType {
  ERROR = 'error',
  INFO = 'info',
  SUCCESS = 'success'
}

export interface Alert {
  alertText: string;
  alertType: AlertType;
}

// RemoveType defines type of item can be removed from Role
export enum RemoveType {
  POLICY = 'policy',
  USER = 'user',
  ROLESCOPE = 'rolescope'
}

export interface RemoveItem {
  type: RemoveType;
  data: IAMUser | Policy;
}

export enum PolicyEffectType {
  ALLOW = 'allow',
  DENY = 'deny'
}

export interface ResourceAction {
  resource: StatementResource;
  action: StatementAction;
  service: StatementService;
}

export interface StatementService {
  id: string;
  name: string;
}

export interface StatementResource {
  id: string;
  name: string;
}

export interface StatementAction {
  id: string;
  name: string;
}

export interface PolicyFormDetails {
  name: string;
  description: string;
}

export interface StatementDraft {
  statement_id?: string;
  effect: PolicyEffectType;
  resource_id: string;
  actions: string[];
}

export interface PolicyDraft extends PolicyFormDetails {
  statements: StatementDraft[];
}

export interface RoleObject {
  object_role_id: string;
  role_id: string;
  object_id: string;
  object_name: string;
}

export interface PlatformService {
  id: string;
  name: string;
}

export interface ServiceRole {
  service_role_id: string;
  role_id: string;
  service_id: string;
  service_name: string;
}



// WEBPACK FOOTER //
// ./src/app/core/models/index.ts